import { z } from 'zod';
import { DeliveryChannelSchema, IsoDateTimeSchema, NonEmptyTrimmedSchema, UuidSchema } from './common.js';
import { PreferredSourceEntrySchema, type PreferredSourceEntry } from './project.js';

export const BriefStatusSchema = z.enum([
  'queued',
  'researching',
  'drafting',
  'delivering',
  'completed',
  'failed',
  'cancelled',
]);
export type BriefStatus = z.infer<typeof BriefStatusSchema>;

export const OutlineSectionSchema = z.object({
  h2: NonEmptyTrimmedSchema.max(200),
  h3s: z.array(NonEmptyTrimmedSchema.max(200)).max(15),
  summary: z.string().max(500).optional(),
});
export type OutlineSection = z.infer<typeof OutlineSectionSchema>;

export const OutlineSchema = z.object({
  h1: NonEmptyTrimmedSchema.max(200),
  sections: z.array(OutlineSectionSchema).min(3).max(20),
});
export type Outline = z.infer<typeof OutlineSchema>;

// NOTE: The CreateBriefRequestSchema below re-references BriefPersonalizationSchema
// and BriefInputPinsSchema which are defined further down in this file. Their
// declarations are hoisted via `const` bindings but not their initializer values,
// so we define the schema without those v2 extensions here and then extend it
// after the v2 schemas are declared. See `CreateBriefRequestSchemaBase` +
// `CreateBriefRequestSchema` split at the bottom of the v2 section.
const CreateBriefRequestSchemaBase = z.object({
  project_id: UuidSchema,
  target_keyword: NonEmptyTrimmedSchema.max(200),
  secondary_keywords: z.array(NonEmptyTrimmedSchema.max(200)).max(20).optional(),
  /**
   * Free-mode escape hatch: when DataForSEO SERP isn't configured, pass 3–10
   * competitor URLs to seed the research phase directly. parseCompetitors will
   * crawl these instead of the SERP top-10.
   */
  competitor_urls: z.array(z.string().url()).min(1).max(10).optional(),
  /** Defaults to markdown — Notion / Google Docs / PDF delivery are deprecated. */
  delivery: DeliveryChannelSchema.default('markdown'),
});

// The base Brief row schema (v1 shape). v2 fields are additively extended
// below once the BriefContent / BriefEvidenceCitation / personalization
// schemas are declared.
const BriefSchemaBase = z.object({
  id: UuidSchema,
  project_id: UuidSchema,
  target_keyword: z.string(),
  secondary_keywords: z.array(z.string()),
  status: BriefStatusSchema,
  outline: OutlineSchema.nullable(),
  meta_title_variants: z.array(z.string()).nullable(),
  meta_desc_variants: z.array(z.string()).nullable(),
  cta_variants: z.unknown().nullable(),
  internal_links: z
    .array(
      z.object({
        url: z.string().url(),
        anchor: z.string(),
        score: z.number(),
      }),
    )
    .nullable(),
  delivery_channel: DeliveryChannelSchema,
  delivered_url: z.string().url().nullable(),
  delivered_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  /**
   * Per-activity warnings (e.g. "AI writer degraded — used fallback content").
   * Every activity that gracefully degrades records one here so the UI can
   * explain what happened in plain language without the user reading logs.
   */
  warnings: z
    .array(
      z.object({
        code: z.string(),
        message: z.string(),
      }),
    )
    .default([]),
  created_at: IsoDateTimeSchema,
});

/** LLM brief synthesis output schema (hallucination guard validates against SERP data). */
export const BriefSynthesisOutputSchema = z.object({
  outline: OutlineSchema,
  meta_title_variants: z.array(z.string().min(20).max(70)).length(3),
  meta_desc_variants: z.array(z.string().min(70).max(170)).length(2),
  cta_variants: z.array(
    z.object({
      headline: z.string().min(5).max(120),
      body: z.string().min(10).max(300),
      action_label: z.string().min(2).max(40),
    }),
  ).length(2),
});
export type BriefSynthesisOutput = z.infer<typeof BriefSynthesisOutputSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// A3 v2 — content brief generation schemas
//
// The v2 brief pipeline is: plan_section (fan-out per H2) → draft_brief
// (single reduce) → critique_brief (senior rewrite of qualitative fields).
// Prompts live in apps/workers/src/activities/brief/prompts-v2.ts.
//
// Design notes:
//   • BriefContentSchema is the canonical shape stored in the brief_content
//     jsonb column. It merges the DraftBrief output with critique overrides
//     applied on top.
//   • Personalization + input pins are stored separately so the same brief
//     can be re-generated with different personalization without re-running
//     the upstream A5/A2 pipelines.
//   • Evidence citations use short opaque ids (e.g. "a5p_1", "a5g_3",
//     "paa_2", "a2c_5") for compact wire representation; the resolver
//     lives in the worker layer, not in @seo/types.
// ─────────────────────────────────────────────────────────────────────────────

/** Enum for the source of a single evidence citation. */
export const BriefCitationSourceTypeSchema = z.enum([
  'a5_page',
  'a5_gap',
  'a5_paa',
  'a2_cluster',
  'external',
]);
export type BriefCitationSourceType = z.infer<typeof BriefCitationSourceTypeSchema>;

/**
 * A single evidence citation attached to a talking point / section / claim.
 * `ref` is a source-native identifier (URL for a5_page/external, gap id or
 * title for a5_gap, question string for a5_paa, cluster label for a2_cluster).
 * `quote_snippet` — if present — MUST appear verbatim in the referenced source.
 */
export const BriefEvidenceCitationSchema = z.object({
  id: z.string().min(1).max(64),
  source_type: BriefCitationSourceTypeSchema,
  ref: z.string().min(1).max(1000),
  quote_snippet: z.string().max(500).optional(),
});
export type BriefEvidenceCitation = z.infer<typeof BriefEvidenceCitationSchema>;

// ── (A) plan_section output ─────────────────────────────────────────────────
/**
 * PlanSectionOutputSchema — output of the per-H2 planner (map step).
 * Called 3-15 times per brief, one per H2 heading that survived competitor
 * outline hints. The reduce step (DraftBrief) consumes an array of these.
 */
export const PlanSectionOutputSchema = z.object({
  talking_points: z.array(z.string().min(4).max(400)).min(3).max(8),
  citations: z
    .array(
      z.object({
        source_type: BriefCitationSourceTypeSchema,
        ref: z.string().min(1).max(1000),
        quote_snippet: z.string().max(500).optional(),
      }),
    )
    .max(5),
  evidence_strength: z.enum(['strong', 'moderate', 'weak']),
  word_count_target: z.number().int().min(150).max(800),
  h3_suggestions: z
    .array(
      z.object({
        heading: z.string().min(3).max(200),
        one_line_brief: z.string().min(4).max(300),
      }),
    )
    .max(5),
});
export type PlanSectionOutput = z.infer<typeof PlanSectionOutputSchema>;

// ── (B) draft_brief output — the big reduce ─────────────────────────────────
/**
 * DraftBriefOutputSchema — output of the single reduce call that produces
 * every qualitative + quantitative field of the brief in one shot.
 * Downstream: BriefContentSchema is (roughly) DraftBriefOutput with
 * critique fields overlaid.
 */
export const DraftBriefOutputSchema = z.object({
  executive_summary: z.string().min(400).max(1200),
  intent_analysis: z.object({
    primary: z.enum(['informational', 'navigational', 'commercial', 'transactional']),
    secondary: z
      .enum(['informational', 'navigational', 'commercial', 'transactional'])
      .optional(),
    funnel_stage: z.enum(['awareness', 'consideration', 'decision']),
    reasoning: z.string().min(20).max(1000),
  }),
  target_persona: z.object({
    name: z.string().min(2).max(120),
    role: z.string().min(2).max(200),
    pain_points: z.array(z.string().min(4).max(300)).min(2).max(5),
    goals: z.array(z.string().min(4).max(300)).min(2).max(5),
    objections: z.array(z.string().min(4).max(300)).max(3),
  }),
  strategy: z.object({
    goal: z.string().min(10).max(400),
    format: z.string().min(4).max(200),
    differentiator: z.string().min(10).max(500),
    unique_value_prop: z.string().min(80).max(600),
  }),
  keyword_strategy: z.object({
    primary: z.string().min(2).max(200),
    secondary: z.array(z.string().min(2).max(200)).min(3).max(12),
    semantic_terms: z.array(z.string().min(2).max(200)).min(5).max(20),
    avoid_terms: z.array(z.string().min(2).max(200)).max(5),
  }),
  outline: z.object({
    h1: z.string().min(30).max(90),
    sections: z
      .array(
        z.object({
          h2: z.string().min(10).max(120),
          h3s: z.array(z.string().min(3).max(200)).max(8),
          talking_points: z.array(z.string().min(4).max(400)).min(2).max(6),
          evidence_ids: z.array(z.string().min(1).max(64)).max(5),
          word_count_target: z.number().int().min(150).max(800).optional(),
          priority: z.enum(['critical', 'recommended', 'optional']).optional(),
          // Sprint 3 (4.9): per-section search intent so CTAs anchor to
          // commercial/transactional sections and copy tone can shift.
          intent: z
            .enum([
              'informational',
              'commercial',
              'commercial_investigation',
              'transactional',
              'navigational',
            ])
            .optional(),
        }),
      )
      .min(3)
      .max(15),
  }),
  entities_to_cover: z.array(z.string().min(2).max(120)).min(8).max(30),
  faqs: z
    .array(
      z.object({
        question: z.string().min(6).max(300),
        answer_outline: z.string().min(100).max(500),
        intent: z.enum(['why', 'what', 'how', 'when', 'where', 'comparison']),
        // Sprint 3 (4.5): optional pointer to an outline.sections[].h2 so
        // the writer knows to embed this FAQ inline in that section rather
        // than in the wall-of-FAQs block.
        attached_to_section_h2: z.string().max(200).optional(),
      }),
    )
    .min(3)
    .max(12),
  structure_targets: z.object({
    target_word_count: z.object({
      min: z.number().int().min(100).max(20000),
      max: z.number().int().min(100).max(20000),
    }),
    target_h2_count: z.number().int().min(3).max(30),
    target_media_count: z.number().int().min(0).max(50),
    target_reading_time_minutes: z.number().int().min(1).max(120),
    faq_recommended: z.boolean(),
    table_recommended: z.boolean(),
    video_recommended: z.boolean(),
  }),
  external_sources: z
    .array(
      z.object({
        label: z.string().min(4).max(200),
        // `url_hint` describes the source; not required to be a valid URL
        // because the prompt forbids fabricating URLs — the writer resolves.
        url_hint: z.string().min(4).max(500),
        why: z.string().min(10).max(500),
      }),
    )
    .min(2)
    .max(8),
  eeat_requirements: z.object({
    byline_required: z.boolean(),
    author_credentials: z.string().min(4).max(400),
    first_person_expected: z.boolean(),
    citations_expected: z.number().int().min(0).max(100),
    review_by: z.string().max(200).nullable(),
    // Sprint 3 (4.12) — EEAT enrichment signals.
    original_updated_date_policy: z
      .enum(['monthly', 'quarterly', 'biannual', 'annual', 'event_triggered'])
      .nullable()
      .optional(),
    methodology_note_required: z.boolean().optional().default(false),
    expert_quote_required: z.boolean().optional().default(false),
    first_hand_experience_asset: z.string().max(500).nullable().optional(),
  }),
  schema_recommendations: z.object({
    primary_schema: z.enum([
      'Article',
      'HowTo',
      'FAQPage',
      'ProductReview',
      'Comparison',
      'BlogPosting',
    ]),
    additional_schemas: z.array(z.string().min(2).max(80)).max(10),
    notes: z.string().max(600),
  }),
  meta_title_variants: z.array(z.string().min(40).max(65)).length(5),
  meta_desc_variants: z.array(z.string().min(140).max(160)).length(3),
  url_slugs: z
    .array(
      z
        .string()
        .min(3)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'url_slug must be kebab-case, lowercase'),
    )
    .length(2),
  cta_variants: z
    .array(
      z.object({
        // Sprint 3 (4.11): CTA placement enum widened from 3 → 8 to cover
        // lead magnets, exit intents, and decision-stage blocks.
        placement: z.enum([
          'intro',
          'mid',
          'end',
          'inline_lead_magnet',
          'exit_intent',
          'trust_badge_row',
          'pricing_block',
          'demo_block',
        ]),
        headline: z.string().min(5).max(120),
        body: z.string().min(10).max(300),
        action_label: z.string().min(2).max(40),
      }),
    )
    .min(2)
    .max(3),
  media_recommendations: z
    .array(
      z.object({
        media_type: z.enum(['image', 'diagram', 'table', 'chart', 'video']),
        placement: z.string().min(4).max(200),
        purpose: z.string().min(10).max(400),
        alt_text_hint: z.string().min(4).max(300),
      }),
    )
    .min(3)
    .max(8),
  competitor_benchmark: z
    .array(
      z.object({
        url: z.string().min(4).max(1000),
        word_count: z.number().int().min(0),
        unique_angle: z.string().min(4).max(500),
        strength: z.string().min(4).max(400),
        weakness: z.string().min(4).max(400),
      }),
    )
    .max(5),
  avoid: z.array(z.string().min(4).max(400)).min(3).max(10),
  gaps_to_fill: z.array(
    z.object({
      gap_type: z.enum(['topic', 'question', 'depth', 'media', 'eeat']),
      title: z.string().min(4).max(200),
      why_matters: z.string().min(10).max(500),
    }),
  ),
  publish_checklist: z
    .array(
      z.object({
        category: z.enum(['technical', 'editorial', 'distribution']),
        item: z.string().min(4).max(300),
      }),
    )
    .min(8)
    .max(20),
  internal_link_recommendations: z
    .array(
      z.object({
        url: z.string().url(),
        anchor_text_suggestion: z.string().min(2).max(80),
        section_hint: z.string().min(1).max(200),
      }),
    )
    .min(0)
    .max(10)
    .optional(),
  business_goal_reference: z.string().nullable().optional(),
  // Sprint 3 (docs/status/2026-07-29-brief-improvements.md 4.2): explicit
  // SERP feature targeting — drives intro format (AI Overview → 40-60 word
  // direct answer), FAQ inclusion (PAA), media plan (image/video packs).
  serp_features_targeted: z
    .array(
      z.enum([
        'featured_snippet',
        'paa',
        'ai_overview',
        'image_pack',
        'video_carousel',
        'knowledge_panel',
      ]),
    )
    .max(6)
    .optional(),
  // Sprint 3 (4.7): concrete comparison-table specs for the writer.
  comparison_tables: z
    .array(
      z.object({
        topic: z.string().min(3).max(200),
        columns: z.array(z.string()).min(2).max(8),
        row_hints: z.array(z.string()).min(2).max(15),
        comparison_set_hint: z.string().max(300).optional(),
      }),
    )
    .max(4)
    .optional(),
  // Sprint 3 (4.8): single video spec when structure_targets.video_recommended.
  video_spec: z
    .object({
      topic: z.string().min(5).max(300),
      target_length_minutes: z.number().int().positive().max(60),
      produce_or_embed: z.enum(['produce', 'embed_existing']),
      hosting_hint: z.string().max(200),
      script_outline_bullets: z.array(z.string()).min(3).max(15),
    })
    .nullable()
    .optional(),
  // Sprint 3 (4.10): topic-vs-competitor coverage grid derived from A5
  // per_page_scorecards. Surfaces the writer's actual differentiation
  // opportunity. grid[topicIdx][competitorIdx] is boolean.
  coverage_matrix: z
    .object({
      topics: z.array(z.string()).min(3).max(15),
      competitor_urls: z.array(z.string().url()).min(2).max(6),
      grid: z.array(z.array(z.boolean())),
      our_planned_coverage: z.array(z.boolean()),
    })
    .optional(),
  // Sprint 3 (4.13): explicit refresh cadence hint driven by topic volatility.
  refresh_cadence: z
    .enum(['monthly', 'quarterly', 'biannual', 'annual', 'event_triggered'])
    .nullable()
    .optional(),
  ai_writer_instructions: z.string().min(500).max(2000),
});
export type DraftBriefOutput = z.infer<typeof DraftBriefOutputSchema>;

// ── (C) critique_brief output — senior strategist rewrite ───────────────────
/**
 * CritiqueBriefOutputSchema — rewrite of the highest-signal qualitative
 * fields. Overlaid on top of DraftBriefOutput to produce BriefContentSchema.
 */
export const CritiqueBriefOutputSchema = z.object({
  executive_summary: z.string().min(400).max(1200),
  recommended_angle_rewritten: z.string().min(80).max(1000),
  strategy_rewritten: z.object({
    goal: z.string().min(10).max(400),
    format: z.string().min(4).max(200),
    differentiator: z.string().min(10).max(500),
    unique_value_prop: z.string().min(80).max(600),
  }),
  eeat_notes: z.string().min(100).max(500),
  outline_reordering_suggestions: z
    .array(
      z.object({
        current_h2: z.string().min(4).max(200),
        suggested_order_change: z.enum([
          'move_up',
          'move_down',
          'merge_with_next',
          'delete',
          'split',
        ]),
        reasoning: z.string().min(10).max(500),
      }),
    )
    .max(5),
  top_risks: z.array(z.string().min(10).max(500)).min(2).max(5),
});
export type CritiqueBriefOutput = z.infer<typeof CritiqueBriefOutputSchema>;

// ── Personalization + input pins ────────────────────────────────────────────
/**
 * BriefPersonalizationSchema — user-tunable knobs that bias the brief without
 * re-running upstream A5/A2 pipelines. Stored on the brief row.
 */
export const BriefPersonalizationSchema = z.object({
  brand_voice: z.string().max(1000).nullable(),
  target_audience: z.string().max(500).nullable(),
  funnel_stage: z.enum(['awareness', 'consideration', 'decision']).nullable(),
  tone: z
    .enum(['authoritative', 'conversational', 'technical', 'friendly', 'formal'])
    .nullable(),
  target_length: z
    .object({
      min: z.number().int().min(100).max(20000),
      max: z.number().int().min(100).max(20000),
    })
    .nullable(),
  avoid_claims: z.array(z.string().max(300)).max(20).default([]),
  /**
   * Sprint 2 (docs/status/2026-07-29-brief-improvements.md 3.2):
   * upgraded from `string[]` to structured `{name, url, topic_tags[]}`.
   * Old rows stored as bare URL strings — feed them through
   * `preferredSourcesLegacyMigration` before validation.
   */
  preferred_sources: z.array(PreferredSourceEntrySchema).max(20).default([]),
});
export type BriefPersonalization = z.infer<typeof BriefPersonalizationSchema>;

/**
 * Coerce legacy `preferred_sources` values (bare URL strings) into the new
 * `{name, url, topic_tags[]}` shape so pre-Sprint-2 briefs still parse.
 * No-ops values already in the new shape.
 */
export function preferredSourcesLegacyMigration(
  value: unknown,
): PreferredSourceEntry[] {
  if (!Array.isArray(value)) return [];
  const out: PreferredSourceEntry[] = [];
  for (const entry of value) {
    if (typeof entry === 'string') {
      const trimmed = entry.trim();
      if (!trimmed) continue;
      out.push({ name: trimmed, url: trimmed, topic_tags: [] });
      continue;
    }
    if (entry && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      const name = typeof e['name'] === 'string' ? e['name'] : null;
      const url = typeof e['url'] === 'string' ? e['url'] : null;
      const topicTags = Array.isArray(e['topic_tags'])
        ? (e['topic_tags'] as unknown[]).filter(
            (t): t is string => typeof t === 'string' && t.trim().length > 0,
          )
        : [];
      if (name && url) {
        out.push({ name, url, topic_tags: topicTags });
      }
    }
  }
  return out;
}

/**
 * BriefInputPinsSchema — foreign-key-ish pins to the upstream artefacts that
 * seeded this brief. All optional so the same shape works for both
 * pipeline-driven briefs (all pins set) and free-mode briefs (only
 * competitor_analysis_id, or none).
 */
export const BriefInputPinsSchema = z
  .object({
    competitor_analysis_id: UuidSchema.optional(),
    keyword_run_id: UuidSchema.optional(),
    audit_id: UuidSchema.optional(),
    keyword_id: UuidSchema.optional(),
  })
  .refine((v) => !v.keyword_id || v.keyword_run_id, {
    message: 'input_pins.keyword_run_id required when input_pins.keyword_id is set',
    path: ['keyword_run_id'],
  });
export type BriefInputPins = z.infer<typeof BriefInputPinsSchema>;

// ── BriefContent — canonical stored shape ───────────────────────────────────
/**
 * BriefContentSchema — the canonical shape persisted in the `brief_content`
 * jsonb column. Composition:
 *   • base = DraftBriefOutput (all fields)
 *   • overrides = critique rewrites applied on top of executive_summary,
 *     strategy, and (via recommended_angle_rewritten) the strategy block.
 *   • annotations = critique-only fields the writer sees but the draft
 *     didn't produce: eeat_notes, outline_reordering_suggestions, top_risks,
 *     recommended_angle.
 *   • evidence_registry = the resolved evidence citations that the outline's
 *     evidence_ids point at. Kept alongside so downstream consumers don't
 *     need to re-join to A5/A2 tables.
 *
 * This schema is the single load-bearing contract between the worker
 * (produces) and the API/UI (consumes).
 */
export const BriefContentSchema = DraftBriefOutputSchema.extend({
  /** Critique-derived fields. Present when critique step succeeded. */
  recommended_angle: z.string().min(80).max(1000).nullable(),
  eeat_notes: z.string().min(100).max(500).nullable(),
  outline_reordering_suggestions: z
    .array(
      z.object({
        current_h2: z.string(),
        suggested_order_change: z.enum([
          'move_up',
          'move_down',
          'merge_with_next',
          'delete',
          'split',
        ]),
        reasoning: z.string(),
      }),
    )
    .default([]),
  top_risks: z.array(z.string()).default([]),
  /**
   * Resolved citation registry. outline.sections[].evidence_ids point into
   * this map by BriefEvidenceCitation.id.
   */
  evidence_registry: z.array(BriefEvidenceCitationSchema).default([]),
  /** Snapshot of the pins that produced this content, for provenance. */
  input_pins: BriefInputPinsSchema.default({}),
  /** Snapshot of personalization applied, for provenance. */
  personalization_snapshot: BriefPersonalizationSchema.nullable().default(null),
});
export type BriefContent = z.infer<typeof BriefContentSchema>;

// ── Public exported schemas (v1 + v2 combined) ──────────────────────────────
/**
 * BriefSchema — the wire shape of a brief row exposed by the API. v1 fields
 * (outline, meta_title_variants, …) are preserved unchanged; v2 fields are
 * additive and nullable so old rows deserialize cleanly.
 */
export const BriefSchema = BriefSchemaBase.extend({
  /** v2 canonical content payload; null for legacy briefs generated pre-v2. */
  brief_content: BriefContentSchema.nullable().default(null),
  /**
   * Flat lookup from citation id → citation object; the outline's
   * evidence_ids point into this map. Denormalized from
   * brief_content.evidence_registry for convenient lookup on the client.
   */
  evidence_index: z.record(BriefEvidenceCitationSchema).nullable().default(null),
  /**
   * Per-section version counter, keyed by section_key (usually the H2 slug).
   * Incremented whenever a section is regenerated via the section-refresh
   * endpoint so the UI can render "v2/v3" chips next to stale sections.
   */
  section_versions: z.record(z.number()).default({}),
  /** Pins to the upstream A5/A2 artefacts that seeded this brief. */
  input_pins: BriefInputPinsSchema.default({}),
  /** Personalization currently applied to this brief. Partial — all knobs optional. */
  personalization: BriefPersonalizationSchema.partial().default({}),
  /** Section keys whose upstream evidence has changed since generation. */
  stale_sections: z.array(z.string()).default([]),
  /**
   * Monotonically-incrementing content revision. Bumped on any global
   * regeneration (not per-section). Starts at 1.
   */
  content_version: z.number().int().default(1),
  /**
   * A3 v2 in-app article generation. The AI Writer tab lets the user draft
   * the full article inside the app using the brief's own context; these
   * fields back that flow. When `article_status` is 'none' the user hasn't
   * generated an article yet.
   */
  generated_article_md: z.string().nullable().default(null),
  article_status: z
    .enum(['none', 'generating', 'ready', 'failed'])
    .default('none'),
  article_generated_at: IsoDateTimeSchema.nullable().default(null),
  article_word_count: z.number().int().nullable().default(null),
  article_failure_reason: z.string().nullable().default(null),
  /**
   * Sprint 2 (docs/status/2026-07-29-brief-improvements.md 3.5 + 4.14).
   * `business_goal` is the commercial thesis; the four success-metric
   * fields are quantitative targets tracked post-publication.
   */
  business_goal: z.string().nullable().default(null),
  target_position: z.number().int().nullable().default(null),
  target_monthly_traffic: z.number().int().nullable().default(null),
  primary_conversion_action: z.string().nullable().default(null),
  review_after_days: z.number().int().nullable().default(null),
});
export type Brief = z.infer<typeof BriefSchema>;

/**
 * CreateBriefRequestSchema — v1 + v2 fields. Personalization + input_pins
 * are optional; when omitted the API infers pins ("auto-detect existing
 * analysis") and applies default personalization.
 */
export const CreateBriefRequestSchema = CreateBriefRequestSchemaBase.extend({
  /** User-tunable style/audience/format knobs. All partial. */
  personalization: BriefPersonalizationSchema.partial().optional(),
  /** Explicit pins to upstream A5/A2 runs (skips auto-detect). */
  input_pins: BriefInputPinsSchema.optional(),
  /** "Import + improve" flow: existing article to rewrite/enhance. */
  existing_article_url: z.string().url().optional(),
  /**
   * A6 → A3 linkage: when the brief is created from a calendar item, pass
   * the item id here. The API validates that the item belongs to the same
   * tenant + project, then writes `content_calendar_items.brief_id` after
   * the brief row exists.
   */
  calendar_item_id: UuidSchema.optional(),
  /**
   * Sprint 2 — 1-2 sentence business goal tying the article to revenue
   * (distinct from success metrics below).
   */
  business_goal: z.string().max(500).optional(),
  /** Sprint 2 — target SERP rank (1-100) for the primary keyword. */
  target_position: z.number().int().min(1).max(100).optional(),
  /** Sprint 2 — target monthly organic traffic (non-negative int). */
  target_monthly_traffic: z.number().int().min(0).optional(),
  /** Sprint 2 — human-readable conversion action (e.g. "brokerage inquiry"). */
  primary_conversion_action: z.string().max(200).optional(),
  /** Sprint 2 — positive int; days until the brief should be revisited. */
  review_after_days: z.number().int().min(1).optional(),
});
export type CreateBriefRequest = z.infer<typeof CreateBriefRequestSchema>;

/**
 * Partial patch shape for update-brief flows (Sprint 2). Every field is
 * optional; nulls clear the value. Kept separate from CreateBriefRequestSchema
 * so callers explicitly opt into mutation.
 */
export const UpdateBriefRequestSchema = z.object({
  business_goal: z.string().max(500).nullable().optional(),
  target_position: z.number().int().min(1).max(100).nullable().optional(),
  target_monthly_traffic: z.number().int().min(0).nullable().optional(),
  primary_conversion_action: z.string().max(200).nullable().optional(),
  review_after_days: z.number().int().min(1).nullable().optional(),
  personalization: BriefPersonalizationSchema.partial().optional(),
});
export type UpdateBriefRequest = z.infer<typeof UpdateBriefRequestSchema>;
